import { getMcActiveOrgId, mcSubscribeCloudItem, mcGetCloudItem } from "./mcCloudSync";

export function subscribeSessionRefresh(sessionId, callback) {
  const key = `mc-session-engine:${sessionId}`;
  let latestSavedAt = "";

  function storageHandler(event) {
    if (event.key !== key && event.key !== `mc-live-update:${sessionId}`) return;
    callback?.(event.newValue || null, { source: "storage" });
  }

  window.addEventListener("storage", storageHandler);
  const unsubscribeCloud = mcSubscribeCloudItem(key, (raw, data) => {
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      const savedAt = String(parsed?.savedAt || data?.updatedAt?.toMillis?.() || data?.updatedAt || "");
      if (savedAt && savedAt === latestSavedAt) return;
      latestSavedAt = savedAt;
    } catch (_) {}
    callback?.(raw, { source: "cloud", data });
  });

  return () => {
    window.removeEventListener("storage", storageHandler);
    unsubscribeCloud?.();
  };
}

export function notifySessionRefresh(sessionId) {
  try {
    localStorage.setItem(`mc-live-update:${sessionId}`, String(Date.now()));
  } catch (_) {}
}


const SESSION_LIST_KEY = "mc-session-list";
const ARCHIVE_LIST_KEY = "mc-session-archive";
const SESSION_EVENT = "mc-session-change";

function safeParse(raw, fallback) {
  try { return raw ? JSON.parse(raw) : fallback; } catch (_) { return fallback; }
}

function recordTime(record = {}) {
  const candidates = [record?.updatedAt, record?.startedAt, record?.createdAt, record?.lastEditedAt, record?.archivedAt]
    .filter(Boolean)
    .map((value) => Date.parse(value))
    .filter((time) => Number.isFinite(time));
  return Math.max(...candidates, 0);
}

function readTransitionOverrideIds(key = "") {
  const ids = new Set();
  if (typeof window === "undefined") return ids;
  try {
    const raw = window.localStorage?.getItem(key) || "{}";
    const parsed = safeParse(raw, {});
    Object.entries(parsed || {}).forEach(([id, stamp]) => {
      const time = Date.parse(stamp || "");
      if (id && Number.isFinite(time) && Date.now() - time < 24 * 60 * 60 * 1000) ids.add(String(id).trim());
    });
  } catch (_) {}
  return ids;
}

function readArchivedIds(extraRaw = "") {
  const ids = new Set();
  const add = (rows) => (Array.isArray(rows) ? rows : []).forEach((row) => {
    const id = String(row?.sessionId || "").trim();
    if (id) ids.add(id);
  });
  try { add(safeParse(window.localStorage?.getItem(ARCHIVE_LIST_KEY) || "", [])); } catch (_) {}
  try { add(safeParse(window.sessionStorage?.getItem(ARCHIVE_LIST_KEY) || "", [])); } catch (_) {}
  const scanArchiveKeys = (area) => {
    if (!area) return;
    try {
      for (let i = 0; i < area.length; i += 1) {
        const key = area.key(i);
        if (!key || !key.startsWith("mc-archive-session:")) continue;
        const idFromKey = key.replace("mc-archive-session:", "").trim();
        try {
          const parsed = safeParse(area.getItem(key) || "{}", {});
          const id = String(parsed?.sessionId || idFromKey || "").trim();
          if (id) ids.add(id);
        } catch (_) {
          if (idFromKey) ids.add(idFromKey);
        }
      }
    } catch (_) {}
  };
  try { scanArchiveKeys(window.localStorage); } catch (_) {}
  try { scanArchiveKeys(window.sessionStorage); } catch (_) {}
  if (extraRaw) add(safeParse(extraRaw, []));

  // Archive/restore are state transitions. During the few seconds where one tab
  // has written the active list but another tab is still replaying an older
  // archive snapshot, these overrides prevent the listener from deleting a
  // restored session or resurrecting an archived one.
  readTransitionOverrideIds("__mc_session_restore_overrides").forEach((id) => ids.delete(id));
  readTransitionOverrideIds("__mc_session_archive_overrides").forEach((id) => ids.add(id));
  return ids;
}


function isMeaningfulValue(value) {
  if (value === true) return true;
  if (value === false) return false;
  if (value === 0) return true;
  if (value === null || value === undefined) return false;
  return String(value).trim() !== "";
}

function mergeRecordMetadata(base = {}, incoming = {}) {
  const newer = recordTime(incoming) >= recordTime(base) ? incoming : base;
  const older = newer === incoming ? base : incoming;
  const merged = { ...older, ...newer };

  [
    "storeName", "store", "storeKey", "siteName", "siteId", "siteKey",
    "cycleId", "cycleName",
    "sectionId", "sectionName",
    "sessionType", "countScope", "type",
    "fileName", "startedBy", "lastEditedBy", "startedAt", "createdAt", "updatedAt"
  ].forEach((key) => {
    if (!isMeaningfulValue(merged[key]) && isMeaningfulValue(older[key])) merged[key] = older[key];
  });

  const olderFull = older?.fullSite === true || older?.isFullSite === true || older?.fullCount === true ||
    String(older?.sessionType || older?.countScope || older?.type || "").toLowerCase().includes("full");
  const newerFull = newer?.fullSite === true || newer?.isFullSite === true || newer?.fullCount === true ||
    String(newer?.sessionType || newer?.countScope || newer?.type || "").toLowerCase().includes("full");
  if (olderFull || newerFull) {
    merged.fullSite = true;
    merged.isFullSite = true;
    merged.fullCount = true;
    merged.sessionType = merged.sessionType || "full-count";
    merged.countScope = merged.countScope || "full-site";
    merged.sectionName = "";
    merged.sectionId = "";
  }

  return merged;
}

function mergeSessionList(currentRaw = "", incomingRaw = "", archiveRaw = "") {
  // Merge cloud + local active rows. Cloud snapshots can briefly arrive missing
  // active sessions, which made the Sessions tab look empty/disappearing.
  // Archive/finalize still wins through archivedIds and archived flags.
  const archivedIds = readArchivedIds(archiveRaw);
  const incomingRows = safeParse(incomingRaw, []);
  const currentRows = safeParse(currentRaw, []);
  const byId = new Map();
  [...(Array.isArray(incomingRows) ? incomingRows : []), ...(Array.isArray(currentRows) ? currentRows : [])].forEach((record) => {
    const id = String(record?.sessionId || "").trim();
    if (!id || archivedIds.has(id) || record?.archived || record?.archivedAt || record?.deletedAt) return;
    const status = String(record?.status || "").trim().toLowerCase();
    if (status === "archived" || status === "archive") return;
    const existing = byId.get(id);
    if (!existing) {
      byId.set(id, record);
    } else {
      byId.set(id, mergeRecordMetadata(existing, record));
    }
  });
  return Array.from(byId.values()).sort((a, b) => recordTime(b) - recordTime(a));
}




function readCanonicalLedgerSessions(orgId = "") {
  if (typeof window === "undefined") return [];
  const expected = String(orgId || "").trim();
  if (!expected) return [];
  const byId = new Map();
  const scan = (area) => {
    if (!area) return;
    try {
      for (let i = 0; i < area.length; i += 1) {
        const key = area.key(i);
        if (!key || !key.startsWith("mc-count-ledger:")) continue;
        const idFromKey = key.slice("mc-count-ledger:".length).trim();
        try {
          const payload = safeParse(area.getItem(key) || "", null);
          if (!payload || !Array.isArray(payload?.lines) || !payload.lines.length) continue;
          const meta = payload?.meta || {};
          const sessionId = String(meta.sessionId || idFromKey || "").trim();
          if (!sessionId) continue;
          const recordOrgId = String(meta.tenantOrgId || meta.orgId || meta.organisationId || expected).trim();
          if (recordOrgId !== expected) continue;
          const lifecycle = String(meta.lifecycleState || "").toLowerCase();
          const status = String(meta.status || "Active");
          if (meta.immutable === true || meta.finalizedAt || lifecycle === "finalized" || ["archived", "archive"].includes(status.toLowerCase())) continue;
          const summary = meta.sessionSummary || {};
          const touched = payload.lines.filter((line) => line && (line.counted !== "" || line.waste !== "" || String(line.note || line.notes || "").trim())).length;
          const isFull = meta.fullSite === true || meta.isFullSite === true || meta.fullCount === true || String(meta.sessionType || meta.countScope || "").toLowerCase().includes("full");
          byId.set(sessionId, {
            ...meta, sessionId, id: sessionId, tenantOrgId: expected, orgId: expected, status,
            storeName: meta.storeName || meta.store || "", store: meta.store || meta.storeName || "",
            cycleId: meta.cycleId || meta.cycleKey || meta.cycle?.cycleId || meta.cycle?.id || "",
            cycleName: meta.cycleName || meta.cycle?.cycleName || meta.cycle?.name || "",
            cycleKey: meta.cycleKey || meta.cycleId || "", cycle: meta.cycle || undefined,
            sectionId: isFull ? "" : (meta.sectionId || ""), sectionName: isFull ? "" : (meta.sectionName || ""),
            fullSite: isFull, isFullSite: isFull, fullCount: isFull,
            sessionType: meta.sessionType || (isFull ? "full-count" : "section"),
            countScope: meta.countScope || (isFull ? "full-site" : "section"),
            startedBy: meta.startedBy || meta.createdBy || "User", lastEditedBy: meta.lastEditedBy || meta.startedBy || "User",
            startedAt: meta.startedAt || meta.createdAt || payload.savedAt || "", updatedAt: payload.savedAt || meta.updatedAt || meta.completedAt || meta.startedAt || "",
            totalLines: Number(summary.totalLines ?? meta.totalLines ?? payload.lines.length),
            completedLines: Number(summary.completedLines ?? meta.completedLines ?? touched),
            flaggedCount: Number(summary.flaggedCount ?? summary.flaggedItems ?? meta.flaggedCount ?? 0),
            costExposure: Number(summary.costExposure ?? summary.exposure ?? meta.costExposure ?? meta.exposure ?? 0),
            exposure: Number(summary.exposure ?? summary.costExposure ?? meta.exposure ?? meta.costExposure ?? 0),
            verificationStatus: meta.verificationStatus || (meta.verifiedAt ? "verified" : ""),
            verifiedAt: meta.verifiedAt || "", verifiedBy: meta.verifiedBy || "",
          });
        } catch (_) {}
      }
    } catch (_) {}
  };
  scan(window.localStorage);
  scan(window.sessionStorage);
  return Array.from(byId.values());
}

function readTenantLocalSessions(orgId = "") {
  if (typeof window === "undefined") return [];
  const expected = String(orgId || "").trim();
  if (!expected) return [];
  try {
    const rows = safeParse(window.localStorage?.getItem(SESSION_LIST_KEY) || "", []);
    return (Array.isArray(rows) ? rows : []).filter((record) => {
      const recordOrgId = String(record?.tenantOrgId || record?.orgId || "").trim();
      return Boolean(recordOrgId && recordOrgId === expected);
    });
  } catch (_) {
    return [];
  }
}

function readFreshPendingSessions(orgId = "") {
  if (typeof window === "undefined") return [];
  try {
    const parsed = safeParse(window.localStorage?.getItem("__mc_session_local_pending") || "", {});
    const pendingOrgId = String(parsed?.orgId || "").trim();
    const updatedAt = Date.parse(parsed?.updatedAt || "");
    if (!orgId || pendingOrgId !== String(orgId).trim()) return [];
    if (!Number.isFinite(updatedAt) || Date.now() - updatedAt > 2 * 60 * 1000) return [];
    return (Array.isArray(parsed?.records) ? parsed.records : []).filter((record) => {
      const recordOrgId = String(record?.tenantOrgId || record?.orgId || "").trim();
      return recordOrgId === String(orgId).trim();
    });
  } catch (_) {
    return [];
  }
}

function dispatchLiveSessionEvents(records, orgId = "") {
  if (typeof window === "undefined") return;
  const detail = { type: "cloud-live", records, source: "firestore-listener", orgId: String(orgId || "").trim() };
  try { window.dispatchEvent(new CustomEvent(SESSION_EVENT, { detail })); } catch (_) {}
  try { window.dispatchEvent(new CustomEvent("mc-cloud-sync", { detail })); } catch (_) {}
  try { window.dispatchEvent(new Event("storage")); } catch (_) { try { window.dispatchEvent(new CustomEvent("storage")); } catch (__) {} }
}

export function subscribeSessionListLive(callback) {
  if (typeof window === "undefined") return () => {};
  let unsubscribeItem = null;
  let unsubscribeArchive = null;
  let latestSessionRaw = "";
  let latestArchiveRaw = "";
  let hasReceivedSessionSnapshot = false;
  let hasReceivedNonEmptySessionSnapshot = false;
  const subscriptionOrgId = String(getMcActiveOrgId() || "").trim();

  const isCurrentOrg = () => Boolean(subscriptionOrgId && String(getMcActiveOrgId() || "").trim() === subscriptionOrgId);

  const apply = (incomingRaw = latestSessionRaw) => {
    if (!isCurrentOrg()) return;
    latestSessionRaw = String(incomingRaw || "[]");
    // The Firestore document being observed lives under /orgs/<activeOrg>/morpheus,
    // therefore every row arriving from this listener is already tenant-scoped by
    // its document path. Older session-list rows predate tenantOrgId stamping; stamp
    // those legacy cloud rows here so they are not discarded simply for being old.
    const cloudRows = safeParse(latestSessionRaw, []);
    const tenantCloudRows = (Array.isArray(cloudRows) ? cloudRows : []).map((record) => ({
      ...record,
      tenantOrgId: String(record?.tenantOrgId || record?.orgId || subscriptionOrgId).trim(),
      orgId: String(record?.orgId || record?.tenantOrgId || subscriptionOrgId).trim(),
    }));
    const tenantCloudRaw = JSON.stringify(tenantCloudRows);
    // The active organisation's Firestore session-list is authoritative. Never merge
    // it with the browser's previous mc-session-list value: that cache may belong to
    // the organisation used before logout and was the source of cross-org sessions.
    // Archive data is still applied as a removal filter by mergeSessionList.
    const pendingRows = readFreshPendingSessions(subscriptionOrgId);
    const tenantLocalRows = readTenantLocalSessions(subscriptionOrgId);
    // MC400: the canonical ledger is a read-only discovery safety net. If the
    // routing index is accidentally empty/stale, a non-finalized ledger remains
    // visible instead of disappearing from Sessions after refresh.
    const canonicalLedgerRows = readCanonicalLedgerSessions(subscriptionOrgId);
    const localRaw = JSON.stringify([...tenantLocalRows, ...pendingRows, ...canonicalLedgerRows]);
    // Cloud remains the shared authority, but a transient/lagging empty cloud list
    // must not erase a tenant-stamped local live session that was just committed.
    // Archive/finalize still wins inside mergeSessionList via archivedIds.
    const merged = mergeSessionList(localRaw, tenantCloudRaw, latestArchiveRaw)
      .filter((record) => {
        const tenantOrgId = String(record?.tenantOrgId || record?.orgId || "").trim();
        return Boolean(tenantOrgId && tenantOrgId === subscriptionOrgId);
      });
    const nextRaw = JSON.stringify(merged);
    try {
      if (window.localStorage?.getItem(SESSION_LIST_KEY) !== nextRaw) {
        window.localStorage?.setItem(SESSION_LIST_KEY, nextRaw);
      }
    } catch (_) {
      try { window.sessionStorage?.setItem(SESSION_LIST_KEY, nextRaw); } catch (__) {}
    }
    dispatchLiveSessionEvents(merged, subscriptionOrgId);
    callback?.(merged, { source: "cloud", raw: latestSessionRaw, orgId: subscriptionOrgId });
  };

  if (!subscriptionOrgId) return () => {};

  // COST C2 — Sessions is genuinely live, so use Firestore's native document
  // listeners only while a Sessions/Dashboard consumer is mounted. The previous
  // implementation performed two explicit Firestore reads every 15 seconds even
  // when neither routing document changed. A document listener costs an initial
  // read and then only delivers/bills document changes, while Firestore handles
  // reconnects for us. No interval/polling is required.
  unsubscribeArchive = mcSubscribeCloudItem(ARCHIVE_LIST_KEY, (raw) => {
    if (!isCurrentOrg()) return;
    const archiveRows = safeParse(String(raw || "[]"), []);
    latestArchiveRaw = JSON.stringify(
      (Array.isArray(archiveRows) ? archiveRows : []).filter((record) => {
        const tenantOrgId = String(record?.tenantOrgId || record?.orgId || "").trim();
        return Boolean(tenantOrgId && tenantOrgId === subscriptionOrgId);
      })
    );

    try {
      if (window.localStorage?.getItem(ARCHIVE_LIST_KEY) !== latestArchiveRaw) {
        window.localStorage?.setItem(ARCHIVE_LIST_KEY, latestArchiveRaw);
      }
    } catch (_) {
      try { window.sessionStorage?.setItem(ARCHIVE_LIST_KEY, latestArchiveRaw); } catch (__) {}
    }
    try {
      window.dispatchEvent(
        new CustomEvent("mc-archive-list-change", {
          detail: {
            records: safeParse(latestArchiveRaw, []),
            source: "firestore-archive-listener",
          },
        })
      );
    } catch (_) {}

    // Archive/finalize can remove an item from the live list even if the session
    // routing document itself did not change in the same render tick.
    if (hasReceivedSessionSnapshot) apply(latestSessionRaw || "[]");
  });

  unsubscribeItem = mcSubscribeCloudItem(SESSION_LIST_KEY, (raw) => {
    if (!isCurrentOrg()) return;
    const normalizedRaw = String(raw || "").trim();
    let parsedRows = [];
    try {
      const parsed = normalizedRaw ? JSON.parse(normalizedRaw) : [];
      parsedRows = Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      parsedRows = [];
    }

    // A transient missing snapshot must never erase a previously known-good list.
    if (!normalizedRaw && hasReceivedNonEmptySessionSnapshot) {
      apply(latestSessionRaw);
      return;
    }

    hasReceivedSessionSnapshot = true;
    if (parsedRows.length) hasReceivedNonEmptySessionSnapshot = true;
    latestSessionRaw = normalizedRaw || "[]";
    apply(latestSessionRaw);
  });

  return () => {
    unsubscribeItem?.();
    unsubscribeArchive?.();
    unsubscribeItem = null;
    unsubscribeArchive = null;
  };
}
